/* eslint-disable consistent-return */
/* eslint-disable no-async-promise-executor */
/* eslint-disable no-console */
import { translate } from "@iventis/translations/translation";
import { Content } from "@iventis/translations";
import mapboxgl, { GeoJSONSourceSpecification, Map } from "@iventis/mapbox-gl";
import { ModelRenderStatus } from "@iventis/types/model-types";
import { mapboxKey } from "../constants/mapbox-constants";
import { ThreeDView, frontView, cameraSvg, boundingBoxSvg } from "../constants/mapbox-control-svgs";
import { modelGeoJson, xyAxisGeoJsonLines, zAxisGeoJsonPolygon } from "../constants/mapbox-geojson";
import { xyAxisLayerSpec, zAxisLayerSpec } from "../constants/mapbox-layer-specs";
import { MapboxCustomControl } from "./mapbox-custom-control";
import "@iventis/mapbox-gl/dist/mapbox-gl.css";

if (mapboxgl) {
    mapboxgl.accessToken = mapboxKey;
}

type Model = {
    aabb: {
        max: [number, number, number];
        min: [number, number, number];
    };
};

/** Dimensions extracted from a model's AABB bounding box. AABB x = width, y = height, z = length. */
export type ModelDimensions = { width: number; length: number; height: number };

export type ModelRenderResult = {
    status: ModelRenderStatus;
    dimensions?: ModelDimensions;
};

export class MapboxModelPreview {
    constructor(onCameraClicked: (base64Url) => void) {
        this.onCameraClicked = onCameraClicked;
    }

    public map: Map;

    private resizeMapObserver: ResizeObserver;

    public onCameraClicked: (base64Url) => void;

    private boundingBoxVisible = false;

    private lastModelScaleFactor: number | null = null;

    private lastModel: Model | null = null;

    private static readonly BOUNDING_BOX_LAYER_IDS = ["bb-edge-width", "bb-edge-length", "bb-edge-height", "bb-edge-width-top", "bb-edge-length-top"];

    /** Creates a map and returns a promise when complete */
    async createMap(element: HTMLDivElement) {
        return new Promise<void>((resolve) => {
            this.map = new Map({
                container: element,
                center: [0, 0],
                zoom: 22,
                pitch: 60,
                hash: false,
                preserveDrawingBuffer: true,
                style: { version: 8, sources: {}, layers: [{ id: "background", type: "background", paint: { "background-color": "#D3D3D3" } }] },
            });

            this.map.once("idle", () => {
                this.onResizeMap(element);
                this.AddControls();
                this.AddAxes();
                this.FitToFrontLeftView();
                this.AddSky();
                this.map.addSource("model", modelGeoJson as GeoJSONSourceSpecification);
                resolve();
                this.map.on("remove", () => {
                    this.remove();
                });
            });
        });
    }

    private AddControls() {
        this.map.addControl(new mapboxgl.FullscreenControl(), "top-right");
        this.map.addControl(new MapboxCustomControl(translate(Content.map10.modelPreview.front_left_view), this.FitToFrontLeftView, ThreeDView), "top-right");
        this.map.addControl(new MapboxCustomControl(translate(Content.map10.modelPreview.front_view), this.FitToFrontView, frontView, "0 0 448 512"), "top-right");
        this.map.addControl(
            new MapboxCustomControl(translate(Content.map10.modelPreview.capture_thumbnail), () => this.generateThumbnail().then((url) => this.onCameraClicked(url)), cameraSvg),
            "top-right"
        );
        this.map.addControl(new MapboxCustomControl(translate(Content.map12.model_preview.toggle_bounding_box), () => this.toggleBoundingBox(), boundingBoxSvg), "top-right");
        this.resizeControls();
    }

    private resizeControls() {
        requestAnimationFrame(() => {
            const container = this.map?.getContainer();
            if (container == null) {
                return;
            }

            const buttons = container.querySelectorAll<HTMLButtonElement>(".mapboxgl-ctrl-group button");
            buttons.forEach((btn) => {
                const button = btn;
                button.style.width = "26px";
                button.style.height = "26px";
                button.style.minWidth = "26px";
                button.style.minHeight = "26px";
            });

            const svgs = container.querySelectorAll<SVGElement>(".mapboxgl-ctrl-group button svg");
            svgs.forEach((icon) => {
                const svg = icon;
                svg.style.width = "18px";
                svg.style.height = "18px";
            });
        });
    }

    private AddAxes() {
        this.map.addSource("xy-axis", xyAxisGeoJsonLines as GeoJSONSourceSpecification);
        this.map.addSource("z-axis", zAxisGeoJsonPolygon as GeoJSONSourceSpecification);
        this.map.addLayer(zAxisLayerSpec);
        this.map.addLayer(xyAxisLayerSpec);
    }

    private AddSky() {
        this.map.addLayer({
            id: "sky",
            type: "sky",
        });
    }

    private FitToFrontLeftView() {
        this.map.fitBounds(
            [
                [0.000004, 0.000004],
                [0.00005, 0.00005],
            ],
            { pitch: 70, bearing: 45, duration: 0 }
        );
    }

    private FitToFrontView() {
        this.map.fitBounds(
            [
                [-0.00002, -0.00002],
                [0.00002, 0.0004],
            ],
            { pitch: 90, bearing: 0, duration: 0 }
        );
    }

    public getMap() {
        return this.map;
    }

    public async renderModel(modelUrl: string): Promise<ModelRenderResult> {
        return new Promise<ModelRenderResult>(async (resolve) => {
            this.FitToFrontLeftView();
            // Remove any existing model or layer
            if (this.map.style?.getLayer("modellayer") != null) {
                this.map.removeLayer("modellayer");
            }
            if (this.map.style?.modelManager.getModel("model", "") != null) {
                this.map.removeModel("model");
            }

            // Remove existing bounding box layers if present
            this.removeBoundingBox();

            const model = await this.addModel(modelUrl);
            if (model == null) {
                return resolve({ status: ModelRenderStatus.Error });
            }
            this.lastModel = model;
            const modelScaleFactor = this.calculateModelScaleFactor(model);
            this.lastModelScaleFactor = modelScaleFactor;
            this.map.addLayer({
                id: "modellayer",
                type: "model",
                source: "model",
                layout: {
                    "model-id": "model",
                },
                paint: {
                    "model-scale": [modelScaleFactor, modelScaleFactor, modelScaleFactor],
                },
            });
            const found = await this.queryRenderedModel();
            const dimensions = this.extractDimensions(model);
            if (found) {
                this.addBoundingBox();
                this.boundingBoxVisible = true;
                return resolve({ status: ModelRenderStatus.Visible, dimensions });
            }

            return resolve({ status: ModelRenderStatus.Error });
        });
    }

    public generateThumbnail(): Promise<string> {
        return new Promise<string>((resolve) => {
            this.map.setLayoutProperty("z-axis", "visibility", "none");
            this.map.setLayoutProperty("xy-axis", "visibility", "none");
            this.map.setLayoutProperty("sky", "visibility", "none");
            this.map.setPaintProperty("background", "background-color", "white");
            // Hide bounding box edges for thumbnail
            const visibleBbEdges: string[] = [];
            MapboxModelPreview.BOUNDING_BOX_LAYER_IDS.forEach((id) => {
                if (this.map.style?.getLayer(id) != null) {
                    visibleBbEdges.push(id);
                    this.map.setLayoutProperty(id, "visibility", "none");
                }
            });

            setTimeout(() => {
                const thumbnail = this.map.getCanvas().toDataURL();
                this.map.setLayoutProperty("z-axis", "visibility", "visible");
                this.map.setLayoutProperty("xy-axis", "visibility", "visible");
                this.map.setLayoutProperty("sky", "visibility", "visible");
                this.map.setPaintProperty("background", "background-color", "#D3D3D3");
                // Restore bounding box edges
                visibleBbEdges.forEach((id) => {
                    this.map.setLayoutProperty(id, "visibility", "visible");
                });
                return resolve(thumbnail);
            }, 400);
        });
    }

    /** Extracts real-world dimensions from the model AABB. x=width, y=height, z=length. */
    private extractDimensions(model: Model): ModelDimensions {
        const width = Math.round((model.aabb.max[0] - model.aabb.min[0]) * 100) / 100;
        const height = Math.round((model.aabb.max[1] - model.aabb.min[1]) * 100) / 100;
        const length = Math.round((model.aabb.max[2] - model.aabb.min[2]) * 100) / 100;
        return { width, length, height };
    }

    private toggleBoundingBox() {
        if (this.boundingBoxVisible) {
            this.removeBoundingBox();
            this.boundingBoxVisible = false;
        } else {
            this.addBoundingBox();
            this.boundingBoxVisible = true;
        }
    }

    /**
     * Adds bounding box edges to the map as line layers with axis colors.
     * Uses the scaled AABB to draw edges that match the model preview scale.
     * Width edges (x-axis) = red, Length edges (z-axis) = blue, Height edges (y-axis) = green.
     */
    private addBoundingBox() {
        if (this.lastModel == null || this.lastModelScaleFactor == null) {
            return;
        }

        const sf = this.lastModelScaleFactor;
        const { min, max } = this.lastModel.aabb;

        // Convert scaled model X/Z offsets to lng/lat using Mercator meter units.
        // This keeps bbox footprint aligned with the rendered model scale and placement around the map origin.
        const origin = mapboxgl.MercatorCoordinate.fromLngLat([0, 0], 0);
        const meterInMercatorUnits = origin.meterInMercatorCoordinateUnits();
        const toLngLat = (x: number, z: number): [number, number] => {
            const mercatorPoint = new mapboxgl.MercatorCoordinate(origin.x + x * sf * meterInMercatorUnits, origin.y + z * sf * meterInMercatorUnits, origin.z);
            const lngLat = mercatorPoint.toLngLat();
            return [lngLat.lng, lngLat.lat];
        };

        const [x0z0Lng, x0z0Lat] = toLngLat(min[0], min[2]);
        const [x1z0Lng, x1z0Lat] = toLngLat(max[0], min[2]);
        const [x0z1Lng, x0z1Lat] = toLngLat(min[0], max[2]);
        const [x1z1Lng, x1z1Lat] = toLngLat(max[0], max[2]);

        // Height is y-axis for imported models in this preview.
        const y0 = min[1] * sf;
        const y1 = max[1] * sf;

        // Small square size for vertical-edge extrusions.
        const d = Math.max(Math.abs(x1z0Lng - x0z0Lng), Math.abs(x0z1Lat - x0z0Lat)) * 0.01;

        // Width edges (x-axis, red): lines running along x at the 2 corners of z
        const widthEdges = {
            type: "geojson" as const,
            data: {
                type: "FeatureCollection" as const,
                features: [
                    {
                        type: "Feature" as const,
                        properties: {},
                        geometry: {
                            type: "LineString" as const,
                            coordinates: [
                                [x0z0Lng, x0z0Lat],
                                [x1z0Lng, x1z0Lat],
                            ],
                        },
                    },
                    {
                        type: "Feature" as const,
                        properties: {},
                        geometry: {
                            type: "LineString" as const,
                            coordinates: [
                                [x0z1Lng, x0z1Lat],
                                [x1z1Lng, x1z1Lat],
                            ],
                        },
                    },
                ],
            },
        };

        // Length edges (z-axis, blue): lines running along z at the 2 corners of x
        const lengthEdges = {
            type: "geojson" as const,
            data: {
                type: "FeatureCollection" as const,
                features: [
                    {
                        type: "Feature" as const,
                        properties: {},
                        geometry: {
                            type: "LineString" as const,
                            coordinates: [
                                [x0z0Lng, x0z0Lat],
                                [x0z1Lng, x0z1Lat],
                            ],
                        },
                    },
                    {
                        type: "Feature" as const,
                        properties: {},
                        geometry: {
                            type: "LineString" as const,
                            coordinates: [
                                [x1z0Lng, x1z0Lat],
                                [x1z1Lng, x1z1Lat],
                            ],
                        },
                    },
                ],
            },
        };

        // Height edges (y-axis, green): vertical lines at each (x, z) corner – represented as small fill-extrusion rectangles
        const heightEdges = {
            type: "geojson" as const,
            data: {
                type: "FeatureCollection" as const,
                features: [
                    [x0z0Lng, x0z0Lat],
                    [x1z0Lng, x1z0Lat],
                    [x0z1Lng, x0z1Lat],
                    [x1z1Lng, x1z1Lat],
                ].map(([lng, lat]) => ({
                    type: "Feature" as const,
                    properties: { base: y0, height: y1 },
                    geometry: {
                        type: "Polygon" as const,
                        coordinates: [
                            [
                                [lng - d, lat - d],
                                [lng + d, lat - d],
                                [lng + d, lat + d],
                                [lng - d, lat + d],
                                [lng - d, lat - d],
                            ],
                        ],
                    },
                })),
            },
        };

        const topEdgeThickness = Math.max((y1 - y0) * 0.01, 0.01);

        const widthTopEdges = {
            type: "geojson" as const,
            data: {
                type: "FeatureCollection" as const,
                features: [
                    {
                        type: "Feature" as const,
                        properties: { base: y1, height: y1 + topEdgeThickness },
                        geometry: {
                            type: "Polygon" as const,
                            coordinates: [
                                [
                                    [x0z0Lng, x0z0Lat - d],
                                    [x1z0Lng, x1z0Lat - d],
                                    [x1z0Lng, x1z0Lat + d],
                                    [x0z0Lng, x0z0Lat + d],
                                    [x0z0Lng, x0z0Lat - d],
                                ],
                            ],
                        },
                    },
                    {
                        type: "Feature" as const,
                        properties: { base: y1, height: y1 + topEdgeThickness },
                        geometry: {
                            type: "Polygon" as const,
                            coordinates: [
                                [
                                    [x0z1Lng, x0z1Lat - d],
                                    [x1z1Lng, x1z1Lat - d],
                                    [x1z1Lng, x1z1Lat + d],
                                    [x0z1Lng, x0z1Lat + d],
                                    [x0z1Lng, x0z1Lat - d],
                                ],
                            ],
                        },
                    },
                ],
            },
        };

        const lengthTopEdges = {
            type: "geojson" as const,
            data: {
                type: "FeatureCollection" as const,
                features: [
                    {
                        type: "Feature" as const,
                        properties: { base: y1, height: y1 + topEdgeThickness },
                        geometry: {
                            type: "Polygon" as const,
                            coordinates: [
                                [
                                    [x0z0Lng - d, x0z0Lat],
                                    [x0z1Lng - d, x0z1Lat],
                                    [x0z1Lng + d, x0z1Lat],
                                    [x0z0Lng + d, x0z0Lat],
                                    [x0z0Lng - d, x0z0Lat],
                                ],
                            ],
                        },
                    },
                    {
                        type: "Feature" as const,
                        properties: { base: y1, height: y1 + topEdgeThickness },
                        geometry: {
                            type: "Polygon" as const,
                            coordinates: [
                                [
                                    [x1z0Lng - d, x1z0Lat],
                                    [x1z1Lng - d, x1z1Lat],
                                    [x1z1Lng + d, x1z1Lat],
                                    [x1z0Lng + d, x1z0Lat],
                                    [x1z0Lng - d, x1z0Lat],
                                ],
                            ],
                        },
                    },
                ],
            },
        };

        this.map.addSource("bb-edge-width", widthEdges as GeoJSONSourceSpecification);
        this.map.addSource("bb-edge-length", lengthEdges as GeoJSONSourceSpecification);
        this.map.addSource("bb-edge-height", heightEdges as GeoJSONSourceSpecification);
        this.map.addSource("bb-edge-width-top", widthTopEdges as GeoJSONSourceSpecification);
        this.map.addSource("bb-edge-length-top", lengthTopEdges as GeoJSONSourceSpecification);

        this.map.addLayer({
            id: "bb-edge-width",
            type: "line",
            source: "bb-edge-width",
            paint: { "line-color": "red", "line-width": 2 },
        });

        this.map.addLayer({
            id: "bb-edge-length",
            type: "line",
            source: "bb-edge-length",
            paint: { "line-color": "green", "line-width": 2 },
        });

        this.map.addLayer({
            id: "bb-edge-width-top",
            type: "fill-extrusion",
            source: "bb-edge-width-top",
            paint: {
                "fill-extrusion-color": "red",
                "fill-extrusion-height": ["get", "height"],
                "fill-extrusion-base": ["get", "base"],
                "fill-extrusion-opacity": 1,
            },
        });

        this.map.addLayer({
            id: "bb-edge-length-top",
            type: "fill-extrusion",
            source: "bb-edge-length-top",
            paint: {
                "fill-extrusion-color": "green",
                "fill-extrusion-height": ["get", "height"],
                "fill-extrusion-base": ["get", "base"],
                "fill-extrusion-opacity": 1,
            },
        });

        this.map.addLayer({
            id: "bb-edge-height",
            type: "fill-extrusion",
            source: "bb-edge-height",
            paint: {
                "fill-extrusion-color": "blue",
                "fill-extrusion-height": ["get", "height"],
                "fill-extrusion-base": ["get", "base"],
                "fill-extrusion-opacity": 1,
            },
        });
    }

    private removeBoundingBox() {
        MapboxModelPreview.BOUNDING_BOX_LAYER_IDS.forEach((id) => {
            if (this.map.getLayer(id) != null) {
                this.map.removeLayer(id);
            }
            if (this.map.getSource(id) != null) {
                this.map.removeSource(id);
            }
        });
    }

    /**
     * Adds a model to the map and returns the model or null if it is not loaded within 20 seconds.
     *
     * @param modelUrl - The URL of the model to be added.
     * @returns A promise that resolves to the model if it is loaded within 20 seconds, or null otherwise.
     *
     * @throws Will log an error and resolve to null if there is an issue adding the model.
     */
    private async addModel(modelUrl: string): Promise<Model | null> {
        const interval: NodeJS.Timeout | null = null;
        const timeout: NodeJS.Timeout | null = null;
        return new Promise<Model | null>((resolve) => {
            try {
                this.map.addModel("model", modelUrl);

                const getModel = () => {
                    const model = this.map?.style?.modelManager?.models?.[""]?.model?.model;
                    if (model == null) {
                        return;
                    }
                    clearInterval(interval);
                    clearTimeout(timeout);
                    return resolve(model as Model);
                };

                const interval = setInterval(() => getModel(), 500);

                const timeout = setTimeout(() => {
                    clearInterval(interval);
                    return resolve(null);
                }, 20000);
            } catch (error) {
                if (interval != null) {
                    clearInterval(interval);
                }

                if (timeout != null) {
                    clearTimeout(timeout);
                }
                return resolve(null);
            }
        });
    }

    private async queryRenderedModel(): Promise<boolean> {
        let interval: NodeJS.Timeout;
        let timeout: NodeJS.Timeout;
        return new Promise<boolean>((resolve) => {
            try {
                const getFeatures = () => {
                    const features = this.map.queryRenderedFeatures();
                    if (features.length > 0 && features.filter((f) => f.properties?.id === "gc842f12-071f-5537-a665-bace79d0d5b3").length > 0) {
                        clearInterval(interval);
                        clearTimeout(timeout);
                        return resolve(true);
                    }
                };
                interval = setInterval(() => getFeatures(), 500);
                timeout = setTimeout(() => {
                    clearInterval(interval);
                    return resolve(false);
                }, 5000);
            } catch (error) {
                console.error(error);
                if (interval != null) {
                    clearInterval(interval);
                }
                if (timeout != null) {
                    clearTimeout(timeout);
                }
                return resolve(false);
            }
        });
    }

    private calculateModelScaleFactor(model: Model): number {
        // Determine which axis size is the largest over 4, or which axis size is the cloest to 4.
        const xSize = model.aabb.max[0] - model.aabb.min[0];
        const ySize = model.aabb.max[1] - model.aabb.min[1];
        const zSize = model.aabb.max[2] - model.aabb.min[2];
        const size = Math.max(xSize, ySize, zSize);
        const sf = 4 / size;
        return sf;
    }

    private onResizeMap(mapElement: HTMLDivElement) {
        this.resizeMapObserver = new ResizeObserver(() => {
            this.map.resize();
        });
        this.resizeMapObserver.observe(mapElement);
    }

    remove() {
        this.resizeMapObserver.disconnect();
    }
}
